import { BodyText, HeadlineText } from "@classdojo/web/nessie";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { AT, autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import React from "react";
import type { AnyAction } from "redux";
import { appMetadataRadio, RadioChannel, SendRadioProps } from "src/pods/radio";
import radio from "src/pods/radio/assets/blue-radio.png";
import AppModal from "src/shared/components/AppModal";
import AppPage from "src/shared/components/AppPage";
import MaterialIcon from "src/shared/components/MaterialIcon";
import logEvent from "src/utils/logEvent";
import Audio from "./Audio";

type RadioRemoteProps = {
  displayOpen: (url: string, action?: AnyAction) => void;
  displayClose: VoidFunction;
  remoteClose: VoidFunction;
  sendRadio: (params: SendRadioProps) => void;
  mountPoint: string;
  classId: string;
  channels: RadioChannel[];
  synced?: boolean;
  channel?: RadioChannel;
  currentTrackIndex: number;
  playing: boolean;
  isCasting: boolean;
};
// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class RadioRemote extends React.Component<RadioRemoteProps> {
  componentDidMount() {
    logEvent({
      eventName: "radio.setup.open",
    });
  }

  componentWillUnmount() {
    logEvent({
      eventName: "radio.setup.close",
    });
  }

  render() {
    const { playing, channels, channel, currentTrackIndex, isCasting, classId, mountPoint } = this.props;

    const hasAudioSrc = channel && typeof currentTrackIndex === "number" && channel.tracks[currentTrackIndex].src;

    function renderChannel(this: RadioRemote, channel: RadioChannel) {
      const { title, icon } = channel;
      return (
        <li key={title}>
          <button
            data-name="radio_remote:render_channel:img"
            onClick={() => this.openDisplay(channel)}
            sx={channelStyle}
          >
            <img src={icon} sx={channelIconStyle} alt="" />
            <BodyText sx={channelTitleStyle}>{title}</BodyText>
          </button>
        </li>
      );
    }

    return (
      <AppPage {...appMetadataRadio()} classId={classId} mountPoint={mountPoint}>
        <section sx={containerStyle}>
          <HeadlineText as="h1" sx={headerTitleStyle}>
            Choose a channel to play music!
          </HeadlineText>
          <ul sx={channelsStyle}>{channels.map(renderChannel, this)}</ul>
          {channel && mountPoint === "mobile" && (
            <AppModal
              {...appMetadataRadio()}
              onRequestHide={() => this.closeDisplay()}
              onRequestStart={() => this.props.displayOpen("/apps/display/radio")}
              onRequestStop={() => this.props.displayClose()}
            >
              <section>
                <BodyText kind="tertiary" sx={channelHeaderStyle} disable-tag-translation>
                  <AT>
                    Channel: <span sx={channelHeaderTitleStyle}>{channel.title}</span>
                  </AT>
                </BodyText>
                <div sx={radioImageContainerStyle} onClick={this.togglePlayback}>
                  <img sx={radioImgStyle} src={radio} alt="" />
                </div>

                <div sx={playbackControlStyle}>
                  <div sx={playbackSideButton} onClick={this.nextTrack} aria-label={autoTranslate("Previous")}>
                    <MaterialIcon icon="skip_previous" sx={iconStyle} />
                  </div>
                  <div
                    sx={playbackMainButton}
                    onClick={this.togglePlayback}
                    aria-label={playing ? autoTranslate("Pause") : autoTranslate("Play")}
                  >
                    <MaterialIcon icon={playing ? "pause" : "play_arrow"} />
                  </div>
                  <div sx={playbackSideButton} onClick={this.previousTrack} aria-label={autoTranslate("Next")}>
                    <MaterialIcon icon="skip_next" sx={iconStyle} />
                  </div>
                </div>
              </section>
              {!isCasting && hasAudioSrc && <Audio src={channel.tracks[currentTrackIndex].src} isPlaying={playing} />}
            </AppModal>
          )}
        </section>
      </AppPage>
    );
  }

  openDisplay = (channel: RadioChannel) => {
    const { sendRadio, displayOpen, isCasting } = this.props;
    logEvent({
      eventName: "radio.start",
      eventValue: channel.title.toLowerCase(),
    });
    sendRadio({
      channel,
      currentTrackIndex: Math.floor(Math.random() * channel.tracks.length),
      // is we're casting to a receiver, start playing immediately. if not, we can't play immediately
      // because of webview limitations. audio playback must start from a user touch event, so
      // we start off paused and let the user press play.
      playing: isCasting,
    });
    displayOpen("/apps/display/radio", {
      type: "RECEIVE_RADIO",
      payload: { channel },
    });
  };

  closeDisplay = () => {
    this.props.displayClose();
    this.props.remoteClose();
    this.props.sendRadio({
      channel: null,
    });
  };

  togglePlayback = () => {
    const { synced, playing, sendRadio } = this.props;
    if (synced) {
      sendRadio({
        currentTrackIndex: this.props.currentTrackIndex,
        channel: this.props.channel,
        playing: !playing,
      });
    }
  };

  nextTrack = () => {
    const { synced, sendRadio, currentTrackIndex, channel: currentChannel } = this.props;
    if (synced && currentChannel) {
      sendRadio({
        playing: this.props.playing,
        channel: this.props.channel,
        currentTrackIndex: (currentTrackIndex + 1) % currentChannel.tracks.length,
      });
    }
  };

  previousTrack = () => {
    const { synced, sendRadio, currentTrackIndex, channel: currentChannel } = this.props;
    if (synced && currentChannel) {
      sendRadio({
        playing: this.props.playing,
        channel: this.props.channel,
        currentTrackIndex: (currentTrackIndex + (currentChannel.tracks.length - 1)) % currentChannel.tracks.length,
      });
    }
  };
}

const containerStyle: ThemeUIStyleObject = {
  paddingBottom: RAW_cssValue("20px"),
};

const headerTitleStyle: ThemeUIStyleObject = {
  marginY: "dt_l",
  marginX: "auto",
  textAlign: "center",
};

const channelsStyle: ThemeUIStyleObject = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  margin: "0",
  padding: "0",
  listStyleType: "none",
};

const channelStyle: ThemeUIStyleObject = {
  flex: "0 0 146px",
  width: "146px",
  height: "146px",
  margin: "dt_xs",
  border: "dt_card",
  borderRadius: "dt_radius_s",
  backgroundColor: "dt_background_primary",
  fontSize: "16px",
  textAlign: "center",
  verticalAlign: "middle",
  boxShadow: "dt_shadow_shadezies",
  cursor: "pointer",
  userSelect: "none",
};

const channelTitleStyle: ThemeUIStyleObject = {
  textAlign: "center",
};

const channelIconStyle: ThemeUIStyleObject = {
  display: "block",
  margin: RAW_cssValue("18px auto"),
  height: "72px",
};

const channelHeaderStyle: ThemeUIStyleObject = {
  margin: RAW_cssValue("0 auto"),
  textAlign: "center",
};

const channelHeaderTitleStyle: ThemeUIStyleObject = {
  fontWeight: "600",
};

const radioImageContainerStyle: ThemeUIStyleObject = {
  display: "block",
  position: "relative",
  margin: RAW_cssValue("0 auto"),
  cursor: "pointer",
};

const radioImgStyle: ThemeUIStyleObject = {
  width: "100%",
  padding: RAW_cssValue(40),
};

const playbackControlStyle: ThemeUIStyleObject = {
  margin: RAW_cssValue("0 auto"),
  textAlign: "center",
};

const iconStyle: ThemeUIStyleObject = {
  verticalAlign: "middle",
};

const playbackMainButton: ThemeUIStyleObject = {
  display: "inline-block",
  width: "64px",
  height: "64px",
  margin: "dt_s",
  borderRadius: "dt_radius_m",
  fontSize: "40px",
  lineHeight: "64px",
  textAlign: "center",
  verticalAlign: "middle",
  backgroundColor: RAW_cssValue("#ff5d5a"),
  color: "dt_content_light",
  cursor: "pointer",
};

const playbackSideButton: ThemeUIStyleObject = {
  display: "inline-block",
  width: "30px",
  margin: "dt_s",
  fontSize: "24px",
  lineHeight: "24px",
  verticalAlign: "middle",
  fontWeight: "600",
  color: RAW_cssValue("#8c8c8c"),
  cursor: "pointer",
};
